/**
 * Pure helpers for the in-app guide. Lives in its own file so the
 * Coachmarks component stays HMR-friendly (react-refresh complains
 * when a component file also exports non-components).
 */

export type CoachmarkId = 'first-class' | 'capture-samples' | 'train-model' | 'test-model';

const STORAGE_KEY = 'bryta-coachmarks-v1';

export function readDismissedCoachmarks(): Set<CoachmarkId> {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as { dismissed?: CoachmarkId[] };
    return new Set(parsed.dismissed ?? []);
  } catch {
    return new Set();
  }
}

export function writeDismissedCoachmarks(set: Set<CoachmarkId>): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ dismissed: Array.from(set) }));
  } catch {
    // localStorage can fail in private mode; tips just reappear, no biggie.
  }
}

/** Reset all coachmarks — useful for an "Inspect all again" link or
 *  for a teacher demoing the studio. */
export function resetCoachmarks(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

export interface CoachmarkInputs {
  classCount: number;
  sampleCount: number;
  trained: boolean;
  testedAt: number | null;
}

export function pickActiveCoachmark(
  state: CoachmarkInputs,
  dismissed: Set<CoachmarkId>,
): CoachmarkId | null {
  if (state.classCount < 1 && !dismissed.has('first-class')) return 'first-class';
  if (state.classCount >= 1 && state.sampleCount === 0 && !dismissed.has('capture-samples')) {
    return 'capture-samples';
  }
  if (state.sampleCount > 0 && !state.trained && !dismissed.has('train-model')) {
    return 'train-model';
  }
  if (state.trained && state.testedAt === null && !dismissed.has('test-model')) {
    return 'test-model';
  }
  return null;
}
